import { HttpClient } from '@angular/common/http';
import { inject, OnDestroy, Service, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, map, startWith } from 'rxjs';
import { SubSink } from 'subsink';
import { SxcService } from '../admin/sxc/sxc.service';
import {
  CheckoutLabels,
  LabelsForCatalogResponse,
  ObjectChoiceLabels,
  PaymentLabels,
} from '../core/sxc.models';
import { selectCatalog } from '../root-store/catalog-store/selectors';
import { RootState } from '../root-store/root-state.model';

@Service()
export class CustomLabelsService implements OnDestroy {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly translate = inject(TranslateService);
  private readonly httpClient = inject(HttpClient);

  private subs = new SubSink();
  checkout = signal<CheckoutLabels>({});
  objectChoice = signal<ObjectChoiceLabels>({});
  payment = signal<PaymentLabels>({});

  constructor() {
    this.subs.sink = combineLatest([
      this.store.select(selectCatalog),
      this.translate.onLangChange.pipe(startWith({ lang: this.translate.currentLang })),
    ]).subscribe(([catalog]) => {
      this.getCustomLabels(catalog.id, catalog.parentCatalogId).subscribe((customLabels) => {
        this.checkout.set(customLabels.Checkout);
        this.objectChoice.set(customLabels.ObjectChoice);
        this.payment.set(customLabels.Payment);
      });
    });
  }

  public getCustomLabels(catalogId: number, parentCatalogId?: number) {
    const params: Record<string, string | number> = {
      CatalogId: catalogId,
      appId: SxcService.defaultAppId,
    };

    if (parentCatalogId) {
      params['parentCatalogId'] = parentCatalogId;
    }

    return this.httpClient
      .get<LabelsForCatalogResponse>(`/api/sxc/app/auto/query/GetLabelsForCatalog`, { params })
      .pipe(
        map((res) => ({
          ObjectChoice: res.ObjectChoice[0] ?? {},
          Checkout: res.Checkout[0] ?? {},
          Payment: res.Payment[0] ?? {},
        })),
      );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
